import {DotsLoadingIndicator, SuccessIndicator} from '#/atoms/dots-loading-indicator';
import {NoticeMessageErrorDetails} from '#/atoms/notice-message-error-details';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {
  ActionMenuHeader,
  ActionMenuHeaderButton,
  ActionMenuHeaderIcons,
} from '#/components/actionmenu/action-menu-header';
import {ActionMenuLayoutContent, ActionMenuLayoutFooter} from '#/components/actionmenu/action-menu-layout';
import AsyncButton from '#/components/async-button/component';
import {transactionTypeConstants} from '#/components/transaction/transaction-type-constants';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {TransactionFlow, useTransactionFlowMessage} from '#/hooks/use-transaction-flow';
import {FunctionComponent, ReactNode, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {TransactionType} from 'shareland-common';

export const ActionMenuTransactionLayout: FunctionComponent<{
  transactionType: TransactionType;
  transactionFlow: TransactionFlow;
  startTransactionFlow: () => Promise<void>;
  children: {
    mainInfo: ReactNode;
    overview: ReactNode;
  };
}> = ({transactionType, transactionFlow, startTransactionFlow, children}) => {
  // Context
  const {land, landMarketInfo} = useContext(ActionMenuContext);

  // External hooks
  const {t} = useTranslation('translation');

  const navigate = useNavigate();
  const {
    actionMenu: {close},
  } = useQueryNavigationActions();

  // Vars
  const isNotReady = transactionFlow.status === 'not-ready';
  const isIdle = transactionFlow.status === 'ready';
  const isSuccess = transactionFlow.status === 'success';
  const isPending = !(isIdle || isSuccess || isNotReady);
  const isError = transactionFlow.error !== undefined;

  // Functions
  const actions = {
    headerButtonOnClose: () => {
      navigate(-1);
    },
    footerButtonOnClose: close,
  };

  return (
    <div className='relative h-full flex flex-col items-stretch'>
      <ActionMenuHeader land={land} landMarketInfo={landMarketInfo}>
        <ActionMenuHeaderButton onClick={actions.headerButtonOnClose}>
          <ActionMenuHeaderIcons.Back />
        </ActionMenuHeaderButton>
      </ActionMenuHeader>

      <ActionMenuLayoutContent>
        {(isNotReady || isIdle) && (
          <span className='w-full text-right text-xl text-white font-semibold mt-2'>
            {!isSuccess
              ? t('pages.actionMenu.transaction.titlePreview', {
                  transactionTypeName: t(`transactions.${transactionTypeConstants[transactionType].i18nKey}.present`),
                })
              : t('pages.actionMenu.transaction.titleConfirmation', {
                  transactionTypeName: t(`transactions.${transactionTypeConstants[transactionType].i18nKey}.present`),
                })}
          </span>
        )}
        <div className='h-full flex flex-col justify-between gap-4'>
          {(isNotReady || isIdle) && (
            <>
              {children.mainInfo}
              {transactionFlow.error && <NoticeMessageErrorDetails error={transactionFlow.error} />}
              {children.overview}
            </>
          )}
          {isPending && (
            <div className='grow flex-center text-white'>
              <PendingContent transactionFlow={transactionFlow} />
            </div>
          )}
          {isSuccess && (
            <>
              <div className='grow flex-center text-white'>
                <SuccessIndicator>
                  {t('pages.actionMenu.transaction.titleSuccess', {
                    transactionTypeName: t(`transactions.${transactionTypeConstants[transactionType].i18nKey}.present`),
                  })}
                </SuccessIndicator>
              </div>
              {children.overview}
            </>
          )}
        </div>
      </ActionMenuLayoutContent>

      <ActionMenuLayoutFooter>
        {isSuccess ? (
          <AsyncButton variant='regular' isLoading={false} disabled={false} onClick={close}>
            {t('pages.actionMenu.transaction.ctaBottomButtonSuccess')}
          </AsyncButton>
        ) : isError ? (
          <AsyncButton variant='regular' isLoading={false} disabled={false} onClick={startTransactionFlow}>
            {t('pages.actionMenu.transaction.ctaBottomButtonError')}
          </AsyncButton>
        ) : (
          (isNotReady || isIdle) && (
            <AsyncButton variant='regular' isLoading={false} disabled={isNotReady} onClick={startTransactionFlow}>
              {t('pages.actionMenu.transaction.ctaBottomButtonIdle', {
                transactionTypeName: t(`transactions.${transactionTypeConstants[transactionType].i18nKey}.present`),
              })}
            </AsyncButton>
          )
        )}
      </ActionMenuLayoutFooter>
    </div>
  );
};

const PendingContent: FunctionComponent<{transactionFlow: TransactionFlow}> = ({transactionFlow}) => {
  const {t} = useTranslation('translation');

  const transactionFlowMessage = useTransactionFlowMessage(transactionFlow);

  return (
    <DotsLoadingIndicator>
      {{
        title: t('pages.actionMenu.transaction.titleLoading'),
        subtitle: transactionFlowMessage,
      }}
    </DotsLoadingIndicator>
  );
};
